/**
 * Liveness Detection Module for AppBouncer SDK
 * Simplified offline liveness verification - no backend API calls
 *
 * Thresholds are centralized in liveness-config.ts for easy calibration.
 * Use debug.html to test and calibrate thresholds on different devices.
 */

import {
  computeBrowRaise,
  computeEAR,
  computeMAR,
  computeSmileMetric,
  estimatePitch,
  estimateYaw,
  Landmark,
  sleep,
} from '../face-detection';

import {
  DEFAULT_THRESHOLDS,
  getThreshold,
  getActionWindowMs,
  getActionHoldMs,
  getActionBaselineMs,
  type LivenessAction as ConfigLivenessAction,
} from './config';

import { i18n } from '../i18n';

// ============================================================
// Types
// ============================================================

export type LivenessAction =
  | 'BLINK'
  | 'LOOK_LEFT'
  | 'LOOK_RIGHT'
  | 'SMILE'
  | 'OPEN_MOUTH'
  | 'LOOK_UP'
  | 'LOOK_DOWN'
  | 'RAISE_BROWS';

export interface ActionParams {
  /** Action window in milliseconds (default varies by action) */
  window?: number;
  /** EAR threshold for blink detection (default: 0.18) */
  threshold?: number;
  /** Yaw angle threshold for look actions (default: 8) */
  angle?: number;
  /** Hold duration in milliseconds (default: 600) */
  hold?: number;
  /** User hint text */
  hint?: string;
  /** Returns total ms paused since attempt start (for lighting pause support) */
  getPausedMs?: () => number;
}

export interface ActionResult {
  action: LivenessAction;
  ok: boolean;
  score: number;
  detail: Record<string, unknown>;
}

export interface ChallengeStep {
  action: LivenessAction;
  params?: ActionParams;
}

export interface LivenessConfig {
  /** Number of random actions to perform (default: 2) */
  actionCount?: number;
  /** Maximum retry attempts per action (default: 3) */
  maxAttempts?: number;
  /** Pause between steps in ms (default: 1500) */
  stepPauseMs?: number;
  /** Debug mode */
  debug?: boolean;
}

export interface LivenessCallbacks {
  /** Called when an action prompt should be displayed */
  onPrompt?: (
    action: LivenessAction,
    hint?: string,
    stepIndex?: number,
    totalSteps?: number
  ) => void;
  /** Called to update countdown display */
  onCountdown?: (secondsLeft: number) => void;
  /** Called to clear countdown display */
  onCountdownClear?: () => void;
  /** Called when an action completes */
  onActionComplete?: (result: ActionResult) => void;
  /** Called when status changes */
  onStatusChange?: (status: string) => void;
  /** Called to get current landmarks */
  getLandmarks: () => Landmark[] | null;
}

export interface LivenessVerifyResult {
  success: boolean;
  actions: LivenessAction[];
  results: ActionResult[];
  durationMs: number;
}

// ============================================================
// Available Actions Pool
// ============================================================

const AVAILABLE_ACTIONS: LivenessAction[] = [
  // 'BLINK',       // TODO: re-enable after threshold tuning
  'LOOK_LEFT',
  'LOOK_RIGHT',
  // 'SMILE',       // TODO: re-enable after threshold tuning
  'OPEN_MOUTH',
  // 'LOOK_UP',     // TODO: re-enable after threshold tuning
  // 'LOOK_DOWN',   // TODO: re-enable after threshold tuning
  'RAISE_BROWS',
];

// ============================================================
// Default Configuration
// ============================================================

const DEFAULT_CONFIG: Required<LivenessConfig> = {
  actionCount: 1,
  maxAttempts: 3,
  stepPauseMs: 1500,
  debug: false,
};

// ============================================================
// Action Evaluators
// ============================================================

/**
 * Returns effective elapsed ms, subtracting any paused time.
 * Evaluators use this instead of raw `performance.now() - start`
 * so the action window freezes during lighting pauses.
 */
function activeElapsed(start: number, params: ActionParams): number {
  const pausedMs = params.getPausedMs?.() ?? 0;
  return performance.now() - start - pausedMs;
}

type ActionEvaluator = (
  params: ActionParams,
  getLandmarks: () => Landmark[] | null
) => Promise<ActionResult>;

/**
 * BLINK: Full close and reopen within window
 */
async function evaluateBlink(
  params: ActionParams,
  getLandmarks: () => Landmark[] | null
): Promise<ActionResult> {
  const windowMs = params.window ?? getActionWindowMs('BLINK');
  const threshold = params.threshold ?? getThreshold('blinkEarThreshold');
  const reopenDelta = getThreshold('blinkReopenDelta');
  const start = performance.now();
  let sawLow = false;
  let minEar = 1.0;
  let maxEar = 0.0;
  let nullCount = 0;
  let validCount = 0;

  console.log(`[BLINK] Starting: window=${windowMs}ms, threshold=${threshold}`);

  while (activeElapsed(start, params) < windowMs) {
    const landmarks = getLandmarks();
    if (!landmarks) {
      nullCount++;
      // Log every 10th null to avoid spam
      if (nullCount % 10 === 1) {
        console.log(`[BLINK] ⚠️ No landmarks available (null count: ${nullCount})`);
      }
      await sleep(30);
      continue;
    }

    validCount++;
    const ear = computeEAR(landmarks);
    minEar = Math.min(minEar, ear);
    maxEar = Math.max(maxEar, ear);

    // Log every frame for calibration
    console.log(
      `[BLINK] EAR: ${ear.toFixed(3)} | threshold: ${threshold} | sawLow: ${sawLow} | minEar: ${minEar.toFixed(3)}`
    );

    // Eye closed = low EAR
    if (ear < threshold) {
      if (!sawLow) {
        console.log(`[BLINK] ✓ Eye CLOSED!`);
      }
      sawLow = true;
    }

    // Eye reopened after being closed
    if (sawLow && ear > threshold + reopenDelta) {
      console.log(`[BLINK] ✓✓ SUCCESS! Eye reopened.`);
      return {
        action: 'BLINK',
        ok: true,
        score: 1.0,
        detail: { ear, minEar, maxEar, validCount, nullCount },
      };
    }

    await sleep(30);
  }

  console.log(
    `[BLINK] TIMEOUT. sawLow=${sawLow}, minEar=${minEar.toFixed(3)}, validFrames=${validCount}, nullFrames=${nullCount}`
  );
  return {
    action: 'BLINK',
    ok: false,
    score: 0.0,
    detail: { minEar, maxEar, sawLow, validCount, nullCount },
  };
}

// Yaw smoothing buffer for turn detection (moving average filter)
const YAW_HISTORY_SIZE = DEFAULT_THRESHOLDS.yawHistorySize;
let yawHistory: number[] = [];

/**
 * Get smoothed yaw using moving average to filter MediaPipe jitter
 */
function getSmoothedYaw(currentYaw: number): number {
  yawHistory.push(currentYaw);
  if (yawHistory.length > YAW_HISTORY_SIZE) {
    yawHistory.shift();
  }
  return yawHistory.reduce((a, b) => a + b, 0) / yawHistory.length;
}

/**
 * Reset yaw history (call before starting a new turn detection)
 */
function resetYawHistory(): void {
  yawHistory = [];
}

// Hysteresis margin to prevent hold timer flickering (now from config)
const YAW_HYSTERESIS = DEFAULT_THRESHOLDS.yawHysteresis;

// Pitch smoothing buffer for look up/down detection (moving average filter)
const PITCH_HISTORY_SIZE = DEFAULT_THRESHOLDS.pitchHistorySize;
let pitchHistory: number[] = [];

/**
 * Get smoothed pitch using moving average to filter MediaPipe jitter
 */
function getSmoothedPitch(currentPitch: number): number {
  pitchHistory.push(currentPitch);
  if (pitchHistory.length > PITCH_HISTORY_SIZE) {
    pitchHistory.shift();
  }
  return pitchHistory.reduce((a, b) => a + b, 0) / pitchHistory.length;
}

/**
 * Reset pitch history (call before starting a new look up/down detection)
 */
function resetPitchHistory(): void {
  pitchHistory = [];
}

// Hysteresis for pitch actions (now from config)
const PITCH_HYSTERESIS = DEFAULT_THRESHOLDS.pitchHysteresis;

/**
 * LOOK_LEFT: Turn head left (NEGATIVE yaw) and hold
 * Face-utils: negative yaw = looking left, positive yaw = looking right
 * Uses smoothing and hysteresis for more reliable detection
 */
async function evaluateLookLeft(
  params: ActionParams,
  getLandmarks: () => Landmark[] | null
): Promise<ActionResult> {
  const angle = params.angle ?? getThreshold('lookLeftAngle');
  const hold = params.hold ?? getActionHoldMs('LOOK_LEFT');
  const windowMs = params.window ?? getActionWindowMs('LOOK_LEFT');
  const start = performance.now();
  let holdStart: number | null = null;
  let isInTurnZone = false;
  let maxYaw = 0; // Track maximum turn for diagnostics

  // Reset smoothing buffer
  resetYawHistory();

  console.log(
    `[LOOK_LEFT] Starting: threshold=-${angle}°, hold=${hold}ms, window=${windowMs}ms, hysteresis=${YAW_HYSTERESIS}°`
  );
  console.log(`[LOOK_LEFT] Note: NEGATIVE yaw = looking left, need yaw < -${angle}°`);

  while (activeElapsed(start, params) < windowMs) {
    const landmarks = getLandmarks();
    if (!landmarks) {
      await sleep(30);
      continue;
    }

    const rawYaw = estimateYaw(landmarks);
    const yaw = getSmoothedYaw(rawYaw);
    const holdMs = holdStart ? Math.round(performance.now() - holdStart) : 0;

    // Track minimum yaw (most negative = furthest left turn)
    if (yaw < maxYaw) maxYaw = yaw;

    // Log every frame for calibration
    console.log(
      `[LOOK_LEFT] yaw: ${yaw.toFixed(1)}° | raw: ${rawYaw.toFixed(1)}° | need: < -${angle}° | inZone: ${isInTurnZone} | holdMs: ${holdMs} | maxTurn: ${maxYaw.toFixed(1)}°`
    );

    // Entry: yaw must cross the threshold (NEGATIVE = looking left)
    if (yaw < -angle) {
      if (!isInTurnZone) {
        console.log(`[LOOK_LEFT] ✓ ENTERED turn zone (yaw=${yaw.toFixed(1)}° < -${angle}°)`);
      }
      isInTurnZone = true;
      holdStart = holdStart ?? performance.now();

      if (performance.now() - holdStart > hold) {
        console.log(`[LOOK_LEFT] ✓✓ SUCCESS! Held for ${hold}ms at yaw=${yaw.toFixed(1)}°`);
        return { action: 'LOOK_LEFT', ok: true, score: 1.0, detail: { yaw, rawYaw, maxYaw } };
      }
    }
    // Exit: only reset if clearly out of range (hysteresis)
    else if (yaw > -angle + YAW_HYSTERESIS) {
      if (isInTurnZone) {
        console.log(
          `[LOOK_LEFT] ✗ EXITED turn zone (yaw=${yaw.toFixed(1)}° > ${-angle + YAW_HYSTERESIS}°)`
        );
      }
      isInTurnZone = false;
      holdStart = null;
    }
    // In hysteresis zone: maintain current state

    await sleep(30);
  }

  console.log(`[LOOK_LEFT] TIMEOUT. maxYaw=${maxYaw.toFixed(1)}°, needed < -${angle}°`);
  return { action: 'LOOK_LEFT', ok: false, score: 0.0, detail: { maxYaw, threshold: -angle } };
}

/**
 * LOOK_RIGHT: Turn head right (POSITIVE yaw) and hold
 * Face-utils: negative yaw = looking left, positive yaw = looking right
 * Uses smoothing and hysteresis for more reliable detection
 */
async function evaluateLookRight(
  params: ActionParams,
  getLandmarks: () => Landmark[] | null
): Promise<ActionResult> {
  const angle = params.angle ?? getThreshold('lookRightAngle');
  const hold = params.hold ?? getActionHoldMs('LOOK_RIGHT');
  const windowMs = params.window ?? getActionWindowMs('LOOK_RIGHT');
  const start = performance.now();
  let holdStart: number | null = null;
  let isInTurnZone = false;
  let maxYaw = 0; // Track maximum turn for diagnostics

  // Reset smoothing buffer
  resetYawHistory();

  console.log(
    `[LOOK_RIGHT] Starting: threshold=+${angle}°, hold=${hold}ms, window=${windowMs}ms, hysteresis=${YAW_HYSTERESIS}°`
  );
  console.log(`[LOOK_RIGHT] Note: POSITIVE yaw = looking right, need yaw > +${angle}°`);

  while (activeElapsed(start, params) < windowMs) {
    const landmarks = getLandmarks();
    if (!landmarks) {
      await sleep(30);
      continue;
    }

    const rawYaw = estimateYaw(landmarks);
    const yaw = getSmoothedYaw(rawYaw);
    const holdMs = holdStart ? Math.round(performance.now() - holdStart) : 0;

    // Track maximum yaw (most positive = furthest right turn)
    if (yaw > maxYaw) maxYaw = yaw;

    // Log every frame for calibration
    console.log(
      `[LOOK_RIGHT] yaw: ${yaw.toFixed(1)}° | raw: ${rawYaw.toFixed(1)}° | need: > +${angle}° | inZone: ${isInTurnZone} | holdMs: ${holdMs} | maxTurn: ${maxYaw.toFixed(1)}°`
    );

    // Entry: yaw must cross the threshold (POSITIVE = looking right)
    if (yaw > angle) {
      if (!isInTurnZone) {
        console.log(`[LOOK_RIGHT] ✓ ENTERED turn zone (yaw=${yaw.toFixed(1)}° > +${angle}°)`);
      }
      isInTurnZone = true;
      holdStart = holdStart ?? performance.now();

      if (performance.now() - holdStart > hold) {
        console.log(`[LOOK_RIGHT] ✓✓ SUCCESS! Held for ${hold}ms at yaw=${yaw.toFixed(1)}°`);
        return { action: 'LOOK_RIGHT', ok: true, score: 1.0, detail: { yaw, rawYaw, maxYaw } };
      }
    }
    // Exit: only reset if clearly out of range (hysteresis)
    else if (yaw < angle - YAW_HYSTERESIS) {
      if (isInTurnZone) {
        console.log(
          `[LOOK_RIGHT] ✗ EXITED turn zone (yaw=${yaw.toFixed(1)}° < ${angle - YAW_HYSTERESIS}°)`
        );
      }
      isInTurnZone = false;
      holdStart = null;
    }
    // In hysteresis zone: maintain current state

    await sleep(30);
  }

  console.log(`[LOOK_RIGHT] TIMEOUT. maxYaw=${maxYaw.toFixed(1)}°, needed > +${angle}°`);
  return { action: 'LOOK_RIGHT', ok: false, score: 0.0, detail: { maxYaw, threshold: angle } };
}

/**
 * SMILE: Mouth corners wider than normal
 * Uses baseline comparison for more reliable detection
 */
async function evaluateSmile(
  params: ActionParams,
  getLandmarks: () => Landmark[] | null
): Promise<ActionResult> {
  const threshold = params.threshold ?? getThreshold('smileThreshold');
  const windowMs = params.window ?? getActionWindowMs('SMILE');
  const baselineMs = getActionBaselineMs('SMILE') || 300; // Fallback to 300ms
  let nullCount = 0;
  let validCount = 0;

  console.log(`[SMILE] Starting: threshold=${threshold} (baseline comparison)`);

  // Collect baseline - neutral expression
  const baselineSamples: number[] = [];
  const baselineStart = performance.now();

  while (activeElapsed(baselineStart, params) < baselineMs) {
    const landmarks = getLandmarks();
    if (landmarks) {
      const metric = computeSmileMetric(landmarks);
      baselineSamples.push(metric);
      validCount++;
    } else {
      nullCount++;
    }
    await sleep(30);
  }

  const baseline =
    baselineSamples.length > 0
      ? baselineSamples.reduce((a, b) => a + b, 0) / baselineSamples.length
      : 0;

  console.log(
    `[SMILE] Baseline established: ${baseline.toFixed(3)} (${baselineSamples.length} samples, ${nullCount} null during baseline)`
  );

  // Monitor for smile (increase from baseline)
  const checkStart = performance.now();
  let maxDelta = 0;
  nullCount = 0;
  validCount = 0;

  while (activeElapsed(checkStart, params) < windowMs) {
    const landmarks = getLandmarks();
    if (!landmarks) {
      nullCount++;
      if (nullCount % 10 === 1) {
        console.log(`[SMILE] ⚠️ No landmarks available (null count: ${nullCount})`);
      }
      await sleep(30);
      continue;
    }

    validCount++;
    const smileMetric = computeSmileMetric(landmarks);
    const delta = smileMetric - baseline;
    maxDelta = Math.max(maxDelta, delta);

    // Log every frame for calibration
    console.log(
      `[SMILE] metric: ${smileMetric.toFixed(4)} | delta: ${delta.toFixed(4)} | threshold: ${threshold} | baseline: ${baseline.toFixed(4)}`
    );

    if (delta > threshold) {
      console.log(`[SMILE] ✓✓ SUCCESS!`);
      return {
        action: 'SMILE',
        ok: true,
        score: 1.0,
        detail: { smileMetric, delta, baseline, validCount, nullCount },
      };
    }

    await sleep(30);
  }

  console.log(
    `[SMILE] TIMEOUT. maxDelta=${maxDelta.toFixed(4)}, needed > ${threshold}, validFrames=${validCount}, nullFrames=${nullCount}`
  );
  return {
    action: 'SMILE',
    ok: false,
    score: 0.0,
    detail: { maxDelta, baseline, validCount, nullCount },
  };
}

/**
 * OPEN_MOUTH: MAR high (open mouth)
 */
async function evaluateOpenMouth(
  params: ActionParams,
  getLandmarks: () => Landmark[] | null
): Promise<ActionResult> {
  const threshold = params.threshold ?? getThreshold('openMouthMarThreshold');
  const windowMs = params.window ?? getActionWindowMs('OPEN_MOUTH');
  const start = performance.now();
  let maxMar = 0;

  console.log(`[OPEN_MOUTH] Starting: threshold=${threshold}, window=${windowMs}ms`);

  while (activeElapsed(start, params) < windowMs) {
    const landmarks = getLandmarks();
    if (!landmarks) {
      await sleep(30);
      continue;
    }

    const mar = computeMAR(landmarks);
    maxMar = Math.max(maxMar, mar);

    // Log every frame for calibration
    console.log(
      `[OPEN_MOUTH] MAR: ${mar.toFixed(3)} | threshold: ${threshold} | maxMar: ${maxMar.toFixed(3)}`
    );

    if (mar > threshold) {
      console.log(`[OPEN_MOUTH] ✓✓ SUCCESS!`);
      return { action: 'OPEN_MOUTH', ok: true, score: 1.0, detail: { mar, maxMar } };
    }

    await sleep(30);
  }

  console.log(`[OPEN_MOUTH] TIMEOUT. maxMar=${maxMar.toFixed(3)}, needed > ${threshold}`);
  return { action: 'OPEN_MOUTH', ok: false, score: 0.0, detail: { maxMar } };
}

/**
 * LOOK_UP: Tilt head up (POSITIVE pitch) and hold
 * User calibration: looking up produces pitch ~+37°
 * Uses smoothing and hysteresis for more reliable detection
 */
async function evaluateLookUp(
  params: ActionParams,
  getLandmarks: () => Landmark[] | null
): Promise<ActionResult> {
  const angle = params.angle ?? getThreshold('lookUpAngle');
  const hold = params.hold ?? getActionHoldMs('LOOK_UP');
  const windowMs = params.window ?? getActionWindowMs('LOOK_UP');
  const start = performance.now();
  let holdStart: number | null = null;
  let isInLookZone = false;

  // Reset smoothing buffer
  resetPitchHistory();

  console.log(
    `[LOOK_UP] Starting: angle=${angle}°, hold=${hold}ms, window=${windowMs}ms, hysteresis=${PITCH_HYSTERESIS}°`
  );

  while (activeElapsed(start, params) < windowMs) {
    const landmarks = getLandmarks();
    if (!landmarks) {
      await sleep(30);
      continue;
    }

    const rawPitch = estimatePitch(landmarks);
    const pitch = getSmoothedPitch(rawPitch);
    const holdMs = holdStart ? Math.round(performance.now() - holdStart) : 0;

    // Log every frame for calibration
    console.log(
      `[LOOK_UP] pitch: ${pitch.toFixed(1)}° | raw: ${rawPitch.toFixed(1)}° | threshold: +${angle}° | inZone: ${isInLookZone} | holdMs: ${holdMs}`
    );

    // Entry: pitch must cross the threshold (POSITIVE = looking up)
    if (pitch > angle) {
      if (!isInLookZone) {
        console.log(`[LOOK_UP] ✓ ENTERED look zone`);
      }
      isInLookZone = true;
      holdStart = holdStart ?? performance.now();

      if (performance.now() - holdStart > hold) {
        console.log(`[LOOK_UP] ✓✓ SUCCESS! Held for ${hold}ms`);
        return { action: 'LOOK_UP', ok: true, score: 1.0, detail: { pitch, rawPitch } };
      }
    }
    // Exit: only reset if clearly out of range (hysteresis)
    else if (pitch < angle - PITCH_HYSTERESIS) {
      if (isInLookZone) {
        console.log(`[LOOK_UP] ✗ EXITED look zone (hysteresis: ${angle - PITCH_HYSTERESIS}°)`);
      }
      isInLookZone = false;
      holdStart = null;
    }
    // In hysteresis zone: maintain current state

    await sleep(30);
  }

  console.log(`[LOOK_UP] TIMEOUT. Last pitch position not held long enough.`);
  return { action: 'LOOK_UP', ok: false, score: 0.0, detail: {} };
}

/**
 * LOOK_DOWN: Tilt head down (POSITIVE pitch, lower range than LOOK_UP) and hold
 * User calibration: looking down produces pitch ~+22°, looking up ~+37°
 * Uses smoothing and hysteresis for more reliable detection
 */
async function evaluateLookDown(
  params: ActionParams,
  getLandmarks: () => Landmark[] | null
): Promise<ActionResult> {
  const angle = params.angle ?? getThreshold('lookDownAngle');
  const hold = params.hold ?? getActionHoldMs('LOOK_DOWN');
  const windowMs = params.window ?? getActionWindowMs('LOOK_DOWN');
  const start = performance.now();
  let holdStart: number | null = null;
  let isInLookZone = false;

  // Reset smoothing buffer
  resetPitchHistory();

  console.log(
    `[LOOK_DOWN] Starting: angle=${angle}°, hold=${hold}ms, window=${windowMs}ms, hysteresis=${PITCH_HYSTERESIS}°`
  );

  while (activeElapsed(start, params) < windowMs) {
    const landmarks = getLandmarks();
    if (!landmarks) {
      await sleep(30);
      continue;
    }

    const rawPitch = estimatePitch(landmarks);
    const pitch = getSmoothedPitch(rawPitch);
    const holdMs = holdStart ? Math.round(performance.now() - holdStart) : 0;

    // Log every frame for calibration
    console.log(
      `[LOOK_DOWN] pitch: ${pitch.toFixed(1)}° | raw: ${rawPitch.toFixed(1)}° | threshold: +${angle}° | inZone: ${isInLookZone} | holdMs: ${holdMs}`
    );

    // Entry: pitch must cross the threshold (looking down = lower pitch values)
    if (pitch < angle) {
      if (!isInLookZone) {
        console.log(`[LOOK_DOWN] ✓ ENTERED look zone`);
      }
      isInLookZone = true;
      holdStart = holdStart ?? performance.now();

      if (performance.now() - holdStart > hold) {
        console.log(`[LOOK_DOWN] ✓✓ SUCCESS! Held for ${hold}ms`);
        return { action: 'LOOK_DOWN', ok: true, score: 1.0, detail: { pitch, rawPitch } };
      }
    }
    // Exit: only reset if clearly out of range (head moved back up = higher pitch)
    else if (pitch > angle + PITCH_HYSTERESIS) {
      if (isInLookZone) {
        console.log(`[LOOK_DOWN] ✗ EXITED look zone (hysteresis: ${angle + PITCH_HYSTERESIS}°)`);
      }
      isInLookZone = false;
      holdStart = null;
    }
    // In hysteresis zone: maintain current state

    await sleep(30);
  }

  console.log(`[LOOK_DOWN] TIMEOUT. Last pitch position not held long enough.`);
  return { action: 'LOOK_DOWN', ok: false, score: 0.0, detail: {} };
}

/**
 * RAISE_BROWS: Eyebrows raised above baseline
 * Note: In MediaPipe, Y=0 is top, so brow.y < eye.y (brows above eyes)
 * When raising brows, the delta becomes MORE NEGATIVE (brows move up = lower Y)
 *
 * Detection uses center brow landmarks (105, 334) compared against eye tops (159, 386)
 * which are more sensitive to eyebrow movement than the original inner brow points.
 */
async function evaluateRaiseBrows(
  params: ActionParams,
  getLandmarks: () => Landmark[] | null
): Promise<ActionResult> {
  const threshold = params.threshold ?? getThreshold('raiseBrowsThreshold');
  const windowMs = params.window ?? getActionWindowMs('RAISE_BROWS');
  const baselineMs = getActionBaselineMs('RAISE_BROWS') || 500; // Fallback to 500ms

  console.log(`[RAISE_BROWS] Starting: threshold=${threshold}`);

  // Collect baseline - slightly longer for stability
  const baselineSamples: number[] = [];
  const baselineStart = performance.now();

  while (activeElapsed(baselineStart, params) < baselineMs) {
    const landmarks = getLandmarks();
    if (landmarks) {
      const browDist = computeBrowRaise(landmarks);
      baselineSamples.push(browDist);
    }
    await sleep(30);
  }

  const baseline =
    baselineSamples.length > 0
      ? baselineSamples.reduce((a, b) => a + b, 0) / baselineSamples.length
      : 0;

  console.log(
    `[RAISE_BROWS] Baseline established: ${baseline.toFixed(4)} (${baselineSamples.length} samples)`
  );

  // Monitor for raised brows
  // When brows are raised, they move UP (lower Y value), so delta becomes MORE NEGATIVE
  const checkStart = performance.now();
  let minDelta = 0;

  while (activeElapsed(checkStart, params) < windowMs) {
    const landmarks = getLandmarks();
    if (!landmarks) {
      await sleep(30);
      continue;
    }

    const current = computeBrowRaise(landmarks);
    const browDelta = current - baseline;
    minDelta = Math.min(minDelta, browDelta);

    // Log every frame for calibration
    console.log(
      `[RAISE_BROWS] current: ${current.toFixed(4)} | delta: ${browDelta.toFixed(4)} | threshold: -${threshold} | baseline: ${baseline.toFixed(4)} | minDelta: ${minDelta.toFixed(4)}`
    );

    // Brows raised = delta is NEGATIVE (brows moved up = lower Y)
    if (browDelta < -threshold) {
      console.log(`[RAISE_BROWS] ✓✓ SUCCESS!`);
      return {
        action: 'RAISE_BROWS',
        ok: true,
        score: 1.0,
        detail: { browDelta, current, baseline },
      };
    }

    await sleep(30);
  }

  console.log(`[RAISE_BROWS] TIMEOUT. minDelta=${minDelta.toFixed(4)}, needed < -${threshold}`);
  return { action: 'RAISE_BROWS', ok: false, score: 0.0, detail: { minDelta, baseline } };
}

// Map actions to evaluators
const ACTION_EVALUATORS: Record<LivenessAction, ActionEvaluator> = {
  BLINK: evaluateBlink,
  LOOK_LEFT: evaluateLookLeft,
  LOOK_RIGHT: evaluateLookRight,
  SMILE: evaluateSmile,
  OPEN_MOUTH: evaluateOpenMouth,
  LOOK_UP: evaluateLookUp,
  LOOK_DOWN: evaluateLookDown,
  RAISE_BROWS: evaluateRaiseBrows,
};

// ============================================================
// Liveness Detector Class (Simplified - No API)
// ============================================================

export class LivenessDetector {
  private config: Required<LivenessConfig>;
  private callbacks: LivenessCallbacks | null = null;
  private isRunning = false;
  private _paused = false;
  private _totalPausedMs = 0;
  private _pauseStartTime = 0;

  constructor(config: LivenessConfig = {}) {
    this.config = {
      ...DEFAULT_CONFIG,
      ...config,
    };
  }

  /**
   * Set the number of random liveness actions to perform.
   * Clamped to [1, 5] for safety.
   */
  setActionCount(count: number): void {
    this.config.actionCount = Math.max(1, Math.min(5, count));
  }

  /**
   * Set callbacks for UI updates
   */
  setCallbacks(callbacks: LivenessCallbacks): void {
    this.callbacks = callbacks;
  }

  /**
   * Check if detection is currently running
   */
  isActive(): boolean {
    return this.isRunning;
  }

  /**
   * Pause liveness detection (e.g. when lighting becomes poor).
   * The action timer is frozen and landmarks return null until resumed.
   */
  pause(): void {
    if (!this._paused && this.isRunning) {
      this._paused = true;
      this._pauseStartTime = performance.now();
      console.log('[LivenessDetector] Paused — waiting for lighting to improve');
      this.callbacks?.onStatusChange?.('Improve lighting to continue');
    }
  }

  /**
   * Resume liveness detection after pause.
   */
  resume(): void {
    if (this._paused) {
      const pausedFor = performance.now() - this._pauseStartTime;
      this._totalPausedMs += pausedFor;
      this._paused = false;
      console.log(
        `[LivenessDetector] Resumed after ${pausedFor.toFixed(0)}ms pause (total paused: ${this._totalPausedMs.toFixed(0)}ms)`
      );
    }
  }

  /**
   * Check if currently paused
   */
  isPaused(): boolean {
    return this._paused;
  }

  /**
   * Reset the detector state (call before retrying liveness)
   * This clears the running flag and any internal state
   */
  reset(): void {
    this.isRunning = false;
    this._paused = false;
    this._totalPausedMs = 0;
    this._pauseStartTime = 0;
    // Reset smoothing buffers
    resetYawHistory();
    resetPitchHistory();
    this.log('Liveness detector reset');
  }

  /**
   * Pick N random actions from the pool (no duplicates)
   */
  private pickRandomActions(count: number): LivenessAction[] {
    const shuffled = [...AVAILABLE_ACTIONS].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count);
  }

  /**
   * Run a single action with retries
   */
  private async runAction(
    action: LivenessAction,
    getLandmarks: () => Landmark[] | null,
    stepIndex: number,
    totalSteps: number
  ): Promise<ActionResult> {
    const evaluator = ACTION_EVALUATORS[action];
    if (!evaluator) {
      return { action, ok: false, score: 0, detail: { reason: 'unknown action' } };
    }

    let lastResult: ActionResult | null = null;

    // Wrap getLandmarks to return null when paused (freezes action progress)
    const pauseAwareGetLandmarks = (): Landmark[] | null => {
      if (this._paused) return null;
      return getLandmarks();
    };

    // Show prompt with step information
    const hint = ACTION_HINTS[action];
    this.callbacks?.onPrompt?.(action, hint, stepIndex, totalSteps);
    this.callbacks?.onStatusChange?.(`Perform: ${action.replace(/_/g, ' ')}`);

    // Retry loop
    for (let attempt = 1; attempt <= this.config.maxAttempts; attempt++) {
      this.log(`Action ${action}: Attempt ${attempt}/${this.config.maxAttempts}`);

      // Snapshot paused time before this attempt starts
      const pausedBefore = this._totalPausedMs;

      // Get the correct window for this specific action
      const baseWindowMs = getActionWindowMs(action as ConfigLivenessAction);
      const windowSec = Math.ceil(baseWindowMs / 1000);
      let timerActive = true;

      // Pause-aware countdown: freezes the countdown display when paused
      const timerPromise = (async () => {
        let remaining = windowSec;
        while (remaining >= 0 && timerActive) {
          if (this._paused) {
            await sleep(100);
            continue; // Don't decrement while paused
          }
          this.callbacks?.onCountdown?.(remaining);
          remaining--;
          if (remaining >= 0) await sleep(1000);
        }
      })();

      // Build params with getPausedMs so evaluators can use activeElapsed()
      // Includes any in-progress pause not yet accumulated in _totalPausedMs
      const params: ActionParams = {
        window: baseWindowMs,
        getPausedMs: () => {
          const accumulated = this._totalPausedMs - pausedBefore;
          const currentPause = this._paused ? performance.now() - this._pauseStartTime : 0;
          return accumulated + currentPause;
        },
      };

      // Run evaluator with pause-aware landmarks
      const result = await evaluator(params, pauseAwareGetLandmarks);

      timerActive = false;
      await timerPromise;
      this.callbacks?.onCountdownClear?.();

      lastResult = result;

      if (result.ok) {
        this.callbacks?.onStatusChange?.(`${action.replace(/_/g, ' ')}: ✅ Success!`);
        this.callbacks?.onActionComplete?.(result);
        return result;
      }

      // Failed - show retry message if more attempts
      if (attempt < this.config.maxAttempts) {
        this.callbacks?.onStatusChange?.(`${action.replace(/_/g, ' ')}: ❌ Try again!`);
        await sleep(1000);
      }
    }

    this.callbacks?.onStatusChange?.(`${action.replace(/_/g, ' ')}: ❌ Failed`);
    return lastResult || { action, ok: false, score: 0, detail: {} };
  }

  /**
   * Run full liveness verification (offline, no API calls)
   */
  async verify(): Promise<LivenessVerifyResult> {
    console.log('[LivenessDetector] verify() called');

    // Prevent concurrent runs
    if (this.isRunning) {
      console.warn('[LivenessDetector] Already running - ignoring');
      throw new Error('Liveness verification already in progress');
    }

    if (!this.callbacks?.getLandmarks) {
      throw new Error('getLandmarks callback is required');
    }

    this.isRunning = true;
    const startTime = performance.now();

    try {
      // Pick random actions
      const actions = this.pickRandomActions(this.config.actionCount);
      console.log(`[LivenessDetector] Selected actions: ${actions.join(', ')}`);

      this.callbacks?.onStatusChange?.('Starting liveness check...');

      // Run each action
      const results: ActionResult[] = [];
      const totalSteps = actions.length;
      for (let i = 0; i < actions.length; i++) {
        const action = actions[i];
        console.log(`[LivenessDetector] Running action ${i + 1}/${actions.length}: ${action}`);

        const result = await this.runAction(action, this.callbacks.getLandmarks, i, totalSteps);
        console.log(
          `[LivenessDetector] Action ${action} result:`,
          result.ok ? 'PASSED' : 'FAILED',
          result.detail
        );
        results.push(result);

        // Pause between actions (but not after the last one)
        if (i < actions.length - 1) {
          console.log(`[LivenessDetector] Pausing ${this.config.stepPauseMs}ms before next action`);
          await sleep(this.config.stepPauseMs);
        }
      }

      // Check if all actions passed
      const allPassed = results.every((r) => r.ok);
      const durationMs = performance.now() - startTime;

      console.log(
        `[LivenessDetector] Liveness ${allPassed ? 'PASSED' : 'FAILED'} in ${durationMs.toFixed(0)}ms`
      );
      console.log(
        '[LivenessDetector] Results:',
        results.map((r) => `${r.action}:${r.ok}`).join(', ')
      );

      if (allPassed) {
        this.callbacks?.onStatusChange?.('✅ Liveness verified!');
      } else {
        const failed = results.filter((r) => !r.ok).map((r) => r.action);
        this.callbacks?.onStatusChange?.(`❌ Failed: ${failed.join(', ')}`);
      }

      return {
        success: allPassed,
        actions,
        results,
        durationMs,
      };
    } finally {
      this.isRunning = false;
      console.log('[LivenessDetector] verify() complete, isRunning reset to false');
    }
  }

  private log(...args: unknown[]): void {
    if (this.config.debug) {
      console.log('[LivenessDetector]', ...args);
    }
  }
}

// ============================================================
// Factory Function
// ============================================================

export function createLivenessDetector(config: LivenessConfig = {}): LivenessDetector {
  return new LivenessDetector(config);
}

// ============================================================
// Action Hints (for UI display)
// ============================================================

/**
 * Maps LivenessAction enum values to i18n liveness keys.
 */
const ACTION_I18N_MAP: Record<LivenessAction, string> = {
  BLINK: 'blink',
  LOOK_LEFT: 'lookLeft',
  LOOK_RIGHT: 'lookRight',
  SMILE: 'smile',
  OPEN_MOUTH: 'openMouth',
  LOOK_UP: 'lookUp',
  LOOK_DOWN: 'lookDown',
  RAISE_BROWS: 'raiseBrows',
};

/** English fallbacks used when i18n is not yet initialized. */
const ACTION_HINTS_FALLBACK: Record<LivenessAction, string> = {
  BLINK: 'Blink your eyes',
  LOOK_LEFT: 'Turn your head to the left',
  LOOK_RIGHT: 'Turn your head to the right',
  SMILE: 'Smile',
  OPEN_MOUTH: 'Open your mouth',
  LOOK_UP: 'Look up',
  LOOK_DOWN: 'Look down',
  RAISE_BROWS: 'Raise your eyebrows',
};

/**
 * Get translated hint for a liveness action.
 * Resolves at call time so the current language is used.
 */
export function getActionHint(action: LivenessAction): string {
  const key = ACTION_I18N_MAP[action];
  if (key) {
    const translations = i18n.getTranslations();
    const livenessAction = translations.liveness[key as keyof typeof translations.liveness];
    if (livenessAction && typeof livenessAction === 'object' && 'hint' in livenessAction) {
      return livenessAction.hint;
    }
  }
  return ACTION_HINTS_FALLBACK[action] || action;
}

/**
 * Get translated title for a liveness action.
 */
export function getActionTitle(action: LivenessAction): string {
  const key = ACTION_I18N_MAP[action];
  if (key) {
    const translations = i18n.getTranslations();
    const livenessAction = translations.liveness[key as keyof typeof translations.liveness];
    if (livenessAction && typeof livenessAction === 'object' && 'title' in livenessAction) {
      return livenessAction.title;
    }
  }
  return getActionDisplayName(action);
}

/**
 * Backward-compatible ACTION_HINTS proxy that resolves i18n at access time.
 */
export const ACTION_HINTS: Record<LivenessAction, string> = new Proxy(ACTION_HINTS_FALLBACK, {
  get(_target, prop: string) {
    if (prop in ACTION_I18N_MAP) {
      return getActionHint(prop as LivenessAction);
    }
    return undefined;
  },
});

/**
 * Get human-readable action name
 */
export function getActionDisplayName(action: LivenessAction): string {
  return action
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
